<script setup>
import {computed, onMounted, provide, ref, watch} from "vue";
import NeedsDashboardSidebar from "@/views/enterprise/needsDashboard/NeedsDashboardSidebar.vue";
import NeedsTable from "@/views/enterprise/needsDashboard/NeedsTable.vue";
import needsDashboardService from "@/services/needs-dashboard.service";
import NeedsAIChat from "@/views/needs/chat/NeedsAIChat.vue";
import needsService from "@/services/needs.service";
import NeedsOnboarding from "@/views/needs/NeedsOnboarding.vue";
import EditUser from "@/components/team/EditUser";
import {useStore} from "vue2-helpers/vuex";
import {triggerUpsell} from "@/composables/useStoreActions";

const store = useStore();

const needsTypes = {
    'escalated': 'Escalated Needs',
    'non-escalated': 'Needs in Progress',
    'archived': 'Archived Needs',
}

const dashboardData = ref(null);
const chat = ref(null);
const editUser = ref(null);
const onboardingCompleted = ref(false);
const showArchived = ref(false);
const archivedAnchorClicked = ref(false);

const hasNeeds = computed(() => dashboardData.value?.total);
const hasArchived = computed(() => dashboardData.value?.needs_types['archived']);

const isFree = computed(() => store.getters.onlyNeeds);

watch(() => store.state.needUpdateUsers, value => {
    if (value) {
        getData();
    }
});

onMounted(() => {
    getOnboardingStep();
    getData();
});

async function getOnboardingStep() {
    const {data} = await needsService.getOnboardingStep();

    if (data > 3) {
        onboardingCompleted.value = true;
    }
}

async function getData() {
    const {data} = await needsDashboardService.getDashboardData();
    dashboardData.value = data;
}

function addUser(admin = false) {
    editUser.value.open(0, admin)
}

function upgrade() {
    triggerUpsell(store);
}

function anchorClicked(type) {
    if (type === 'archived') {
        archivedAnchorClicked.value = true;
        showArchived.value = true;
    }
}

provide("dashboardData", dashboardData);
provide("needsTypes", needsTypes);
provide("archivedAnchorClicked", archivedAnchorClicked);
</script>

<template>
    <div>
        <NeedsOnboarding @completed="onboardingCompleted = true"/>

        <template v-if="dashboardData && onboardingCompleted">
            <div class="topbar d-flex justify-content-between">
                <h2 class="heading2 heading2--tertiary w-auto mb-0">Overview</h2>

                <a @click="chat.open()" class="link-create">Create A Need</a>
            </div>

            <div class="need-admin">
                <NeedsDashboardSidebar
                    @addUser="addUser"
                    @upgrade="upgrade"
                />

                <main class="need-admin__main">
                    <template v-if="dashboardData.total">
                        <ul class="need-admin__stat-overview">
                            <li>
                                <big>{{dashboardData.total}}</big>
                                Total Needs
                            </li>
                            <li>
                                <big>{{dashboardData.in_progress}}</big>
                                Sourcing & Evaluating
                            </li>
                            <li>
                                <big>{{dashboardData.escalated}}</big>
                                Escalated Needs
                            </li>
                            <li>
                                <big>{{dashboardData.completed}}</big>
                                Completed
                            </li>
                        </ul>

                        <div class="need-admin__anchors">
                            <template v-for="(label, type) in needsTypes">
                                <a
                                    v-if="dashboardData.needs_types[type]"
                                    :key="type"
                                    :href="`#needs-${type}`"
                                    class="need-admin__anchors__item"
                                    @click="anchorClicked(type)"
                                >
                                    {{label}}
                                </a>
                            </template>
                        </div>

                        <div v-for="(label, type) in needsTypes" :key="type">
                            <NeedsTable v-if="dashboardData.needs_types[type] && (showArchived || type !== 'archived')" :type="type"/>
                        </div>

                        <div v-if="hasArchived && !showArchived" class="text-center pb-5 pt-4">
                            <a @click="showArchived = true" class="link-archive d-inline-flex">View Archived Needs</a>
                        </div>
                    </template>

                    <template v-else>
                        <div class="need-admin__card need-admin__card--round need-admin__card--empty">
                            <div class="need-admin__card__inner">
                                <header class="need-admin__card__header">
                                    <h3 class="need-admin__title">Needs dashboard</h3>
                                </header>

                                <h6 class="heading6">No Needs Have Been Created</h6>

                                <ul class="need-admin__directives">
                                    <li class="need-admin__directives__invite">
                                        <p>Invite teammates to create needs</p>

                                        <a @click="addUser()" class="need-admin__button need-admin__button--bordered">Invite Team Members</a>
                                    </li>
                                    <li class="need-admin__directives__create">
                                        <p>Create A Need Yourself</p>

                                        <a @click="chat.open()" class="need-admin__button need-admin__button--bordered">Create A Need</a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </template>

                    <div v-if="isFree" class="need-admin__summary">
                        <div>
                            <h5 class="heading5 heading5--secondary">
                                <template v-if="hasNeeds">
                                    Ready For More Solutions?
                                </template>
                                <template v-else>
                                    Find over 680,000 solutions
                                </template>
                            </h5>

                            <p>Unlock AI search to identify high-fit startups</p>
                        </div>

                        <a @click="upgrade" class="need-admin__button">
                            <template v-if="hasNeeds">
                                Find More Solutions
                            </template>
                            <template v-else>
                                Find Your Solutions
                            </template>
                        </a>
                    </div>
                </main>
            </div>

            <NeedsAIChat ref="chat"/>
        </template>

        <EditUser ref="editUser"/>
    </div>
</template>

<style scoped>
.need-admin__anchors .need-admin__anchors__item:first-child {
    border-color: #0083b2;
    color: #0083b2;
}
</style>
